/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import { smartDateFormatter, t } from '@superset-ui/core';
import {
  ControlPanelConfig,
  ControlPanelsContainerProps,
  D3_FORMAT_DOCS,
  D3_TIME_FORMAT_OPTIONS,
  sections,
  sharedControls,
  Dataset
} from '@superset-ui/chart-controls';
import { headerFontSize, subheaderFontSize, drillDownType,addDollarSign } from '../sharedControls';
import { commonControlsConfig } from '../../datax/Pie/utils';

function isToolTipFormattingEnabled() {
  return ({ controls }: Pick<ControlPanelsContainerProps, 'controls'>) => Boolean(controls?.enable_tooltip_formatting?.value)
}
export default {
  controlPanelSections: [
    sections.legacyTimeseriesTime,
    {
      label: t('Query'),
      expanded: true,
      controlSetRows: [
        ['metric'],
        [
          {
            name: 'enable_drill_down',
            config: {
              type: 'CheckboxControl',
              label: t('Enable DrillDown'),
              renderTrigger: false,
              default: false,
              description: t('To enable selection of multiple columns for DrillDown Query.'),
            },
          },
        ],
        [
          {
            name: 'drill_down_groupby',
            config: {
              ...sharedControls.groupby,
              multi: true,
              label: t('Drilldown Columns'),
              description: t('Column values to use as categories for each level.'),
              visibility: ({ controls }: ControlPanelsContainerProps) =>
                Boolean(controls?.enable_drill_down?.value),
            },
          },
        ],
        ['adhoc_filters'],
        [ 
          {
            name: 'enable_tooltip_formatting',
            config: {
              type: 'CheckboxControl',
              label: t('Enable ToolTip Formatting'),
              renderTrigger: false,
              default: false,
              description: t('To enable selection of a single column for DrillDown Query.'),
            },
          },
          {
                    name: 'drillDownOrderBy',
                    config: {
                      type: 'SelectControl',
                      label: t('Ordering'),
                      description: t('Order results by selected columns in the tooltip'),
                      multi: true,
                      visibility: isToolTipFormattingEnabled(),
                      default: [],
                      mapStateToProps: ({ datasource }) => ({
                        choices: datasource?.hasOwnProperty('order_by_choices')
                          ? (datasource as Dataset)?.order_by_choices
                          : datasource?.columns || [],
                      }),
                      resetOnHide: false,
                    },
          },
          {
            name: 'drill_down_selected_columns',
            config: {
              ...sharedControls.groupby,
              multi: true,
              label: t('Selected Columns'),
              description: t('Selected columns will be displayed in the drill down table'),
              visibility: isToolTipFormattingEnabled()
            }
          }
        ]
      ],
    },
    {
      label: t('Display settings'),
      expanded: true,
      tabOverride: 'data',
      controlSetRows: [
        [
          {
            name: 'subheader',
            config: {
              type: 'TextControl',
              label: t('Subheader'),
              renderTrigger: true,
              description: t(
                'Description text that shows up below your Big Number',
              ),
            },
          },
        ],
      ],
    },
    {
      label: t('Chart Options'),
      expanded: true,
      controlSetRows: [
        [headerFontSize],
        [subheaderFontSize],
        [addDollarSign],
        ['y_axis_format'],
        [
          {
            name: 'time_format',
            config: {
              type: 'SelectControl',
              freeForm: true,
              label: t('Date format'),
              renderTrigger: true,
              choices: D3_TIME_FORMAT_OPTIONS,
              description: D3_FORMAT_DOCS,
              default: smartDateFormatter.id,
            },
          },
        ],
        [
          {
            name: 'force_timestamp_formatting',
            config: {
              type: 'CheckboxControl',
              label: t('Force date format'),
              renderTrigger: true,
              default: false,
              description: t(
                'Use date formatting even when metric value is not a timestamp',
              ),
            },
          },
        ],
        [
          {
            name: 'tooltipFormat',
            config: {
              ...commonControlsConfig.tooltipFormat,
              default: '{a} <br/>{b} : {c} ({d}%)',
            },
          }
        ],
        [drillDownType]
      ],
    },
  ],
  controlOverrides: {
    y_axis_format: {
      label: t('Number format'),
    },
  },
  denormalizeFormData: formData => ({
    ...formData,
    metric: formData.standardizedFormData.standardizedState.metrics[0],
  }),
  updateStandardizedState: (prevState, currState) => ({
    ...currState,
    metrics: [currState.metrics[0], ...prevState.metrics.slice(1)],
  }),
} as ControlPanelConfig;
